import React, { Suspense, lazy, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMessageCircle } from 'react-icons/fi';
import { useLanguage } from '../../../contexts/LanguageContext';
import { trackEvent } from '../../../services/analytics/analyticsService';

// The panel — flow graph, bubbles, category chips — is a few dozen KB that
// most visitors never open. Splitting it out leaves only this button in the
// main bundle; the rest downloads on the first hover or click.
const HelpChatPanel = lazy(() => import('./HelpChatPanel'));
const preloadPanel = () => import('./HelpChatPanel');

/**
 * Routes where the floating button must not appear.
 *
 * The two form steps — checkout and payment — are where a widget hovering
 * over the page costs money: it overlaps card fields on small screens and
 * gives an already-hesitant buyer something else to click.
 *
 * Matched exactly rather than by prefix, because /checkout/success and
 * /checkout/failed are the opposite case: an order that just went through
 * (or just didn't) is exactly when someone has a question, and there is no
 * form left to obstruct.
 *
 * Sign-in and registration are focused, single-task screens with their own
 * layout (no nav or footer), so the button stays off those too.
 *
 * Product detail pages (/product/:pharmacode, matched by prefix) pin their
 * own add-to-cart bar at the bottom of the screen, which the bubble would
 * sit on top of.
 */
const HIDDEN_PATHS = ['/checkout', '/payment', '/loginpage', '/registerpage'];
const HIDDEN_PREFIXES = ['/product/'];

/**
 * Guided help chat.
 *
 * Mounted once in App.js, so it is available on every page rather than only
 * on the two that used to render the old InfoModal. Nothing here is AI — see
 * chatFlow.js for the full content graph and the reasoning behind its shape.
 */
export default function HelpChat() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // Once opened, the panel stays mounted so its conversation survives closing
  // and reopening within a page view.
  const [everOpened, setEverOpened] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setEverOpened(true);
    trackEvent('help_chat_open', {});
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  // Trailing slashes are normalised so '/checkout/' hides the button too.
  if (
    HIDDEN_PATHS.includes(pathname.replace(/\/+$/, '') || '/') ||
    HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  ) {
    return null;
  }

  return (
    <>
      {/* Below the `custom` breakpoint (1057px) the button clears the mobile
          tab bar (~66px), which grows by the safe-area inset on phones with a
          home indicator or gesture bar. From `custom` up that bar is hidden,
          so it drops to the usual corner inset. z-[9997] keeps it under both
          the tab bar and the cookie consent banner. Solid brand red with a
          white ring so it stays distinct on the site's red sections. */}
      <motion.button
        type="button"
        onClick={handleOpen}
        onMouseEnter={preloadPanel}
        onFocus={preloadPanel}
        className="group fixed bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] right-5 z-[9997] flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white ring-4 ring-white shadow-[0_18px_36px_-12px_rgb(var(--brand)/0.75)] transition-colors hover:bg-brand-hover focus:outline-none focus-visible:ring-red-200 custom:bottom-6 custom:right-6"
        aria-label={t('helpChat.openLabel')}
        aria-haspopup="dialog"
        aria-expanded={open}
        // Fades out while the panel is open: on desktop the card sits in the
        // same corner, and the button peeked out past its rounded edge.
        initial={{ opacity: 0, scale: 0.6 }}
        animate={open ? { opacity: 0, scale: 0.6 } : { opacity: 1, scale: 1 }}
        style={{ pointerEvents: open ? 'none' : 'auto' }}
        transition={{ type: 'spring', stiffness: 260, damping: 20, delay: open ? 0 : 0.4 }}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.94 }}
      >
        <FiMessageCircle size={23} />
      </motion.button>

      {everOpened && (
        // No fallback: the button has already been pressed, and a spinner
        // flashing in the corner for one frame reads as a glitch. The panel
        // simply appears when its chunk lands.
        <Suspense fallback={null}>
          <HelpChatPanel open={open} onClose={handleClose} />
        </Suspense>
      )}
    </>
  );
}
